// React
import { useState } from "react";

// Amplify
import { signUp } from "aws-amplify/auth";

// Material UI
import {
  Container,
  Button,
  Box,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import { MailOutline, LockOutlined } from "@mui/icons-material";

// Formik
import { useFormik } from "formik";

// Components
import ConfirmSignUp from "../ConfirmSignUp";
import PasswordField from "Components/PasswordField";
import { FormikTextField } from "Common/Components";

// Redux
import { setUserEmail } from "../../../Store/AuthSlice";
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// Styles
import { styles } from "./styles";

// Validation
import { signupValidationSchema } from "Validation/UserValidation";

import { useTranslation } from "react-i18next";

const SignUp = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const handleSignUp = async (email, password) => {
    try {
      await signUp({
        username: email,
        password,
        attributes: { email },
      });
      dispatch(setUserEmail(email));
      setConfirmOpen(true);
    } catch (error) {
      switch (error.name) {
        case "UsernameExistsException":
          dispatch(
            openSnackbar({
              message: t("signUp.userExists"),
              severity: "error",
            })
          );
          break;
        default:
          dispatch(openSnackbar({ message: error.message, severity: "error" }));
          break;
      }
    }
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      termsAndConditions: false,
    },
    validationSchema: signupValidationSchema(t),
    onSubmit: (values) => {
      handleSignUp(values.email, values.password);
    },
  });

  // Dynamically select Terms of Use link based on the language
  const termsOfUseLink =
    i18n.language === "fr"
      ? "https://sharedhealthmb.ca/wp-content/uploads/Medical-Navigator-Terms-of-Use-French.pdf"
      : "https://sharedhealthmb.ca/wp-content/uploads/Medical-Navigator-Terms-of-Use.pdf";

  return (
    <Container sx={styles.noPadding}>
      <Box component="form" onSubmit={formik.handleSubmit} sx={styles.formBox}>
        <Box sx={styles.textFieldsBox}>
          <FormikTextField
            formik={formik}
            name="email"
            field={{
              type: "email",
              label: t("signUp.email"),
              required: false,
              slotProps: {
                htmlInput: { "aria-required": true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutline />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
          <PasswordField
            formik={formik}
            name="password"
            autoComplete="new-password"
          />
          <FormikTextField
            formik={formik}
            name="confirmPassword"
            field={{
              type: "password",
              label: t("signUp.confirmPassword"),
              required: false,
              autoComplete: "confirm-password",
              slotProps: {
                htmlInput: { "aria-required": true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  id="termsAndConditions"
                  name="termsAndConditions"
                  checked={formik.values.termsAndConditions}
                  onChange={formik.handleChange}
                  sx={styles.checkbox}
                />
              }
              label={
                <address align={"left"}>
                  {t("signUp.prefix")}
                  <a
                    href={termsOfUseLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("signUp.link")}
                  </a>
                </address>
              }
            />
            {formik.touched.termsAndConditions &&
              Boolean(formik.errors.termsAndConditions) && (
                <FormHelperText error sx={{ mx: "32px" }}>
                  {formik.errors.termsAndConditions}
                </FormHelperText>
              )}
          </Box>
        </Box>
        <Box>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={styles.signupBtn}
          >
            {t("signUp.signUp")}
          </Button>
        </Box>
      </Box>

      <ConfirmSignUp
        open={confirmOpen}
        onClose={handleConfirmClose}
        password={formik.values.password}
      />
    </Container>
  );
};

export default SignUp;
